"use client";

import React, { createContext, useContext, PropsWithChildren } from "react";
import { FeatureFlag, PublicFeatureFlags } from "@/types/feature-flag";
import { useQuery } from "@tanstack/react-query";
import { getPublicFlags, PUBLIC_FEATURE_FLAGS_GET } from "@/api";
import { LoadingSpinner } from "@focal/ui-kit";

export interface FeatureFlagContextType {
  getPublicFeatureFlag: (featureName: PublicFeatureFlags) => FeatureFlag;
}
const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

export const useFeatureFlagContext = () => {
  const context = useContext(FeatureFlagContext);
  if (context === null) {
    throw new Error(
      "useFeatureFlagContext must be used within a FeatureFlagProvider",
    );
  }
  return context;
};

export const FeatureFlagProvider = ({ children }: PropsWithChildren) => {
  const { data: featureFlagsData, isPending } = useQuery<
    Record<string, FeatureFlag>
  >({
    queryKey: [PUBLIC_FEATURE_FLAGS_GET],
    queryFn: () => getPublicFlags(),
    staleTime: 1000 * 60 * 3,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 300,
  });

  const getPublicFeatureFlag = (
    featureName: PublicFeatureFlags,
  ): FeatureFlag => {
    return (
      featureFlagsData?.[featureName] || { value: "control", config: null }
    );
  };

  if (isPending) {
    return (
      <div className="flex flex-1 h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <FeatureFlagContext.Provider value={{ getPublicFeatureFlag }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};
